<template>
    <DButton
        :loading="loading"
        data-cy="url-auth-start"
        size="lg"
        wide
        @click="startBrowserAuth"
    >
        Sign in
    </DButton>
</template>

<script setup>
    import { DButton } from '@digistorm/spark'
    import { preferences } from '@/services'
    import { usePlatformAuthStore } from '@/stores/platformAuth'
    import { usePlatformAuthUrlStore } from '@/stores/platformAuthUrl'

    const emit = defineEmits(['loading'])

    const props = defineProps({
        authRequirements: Object,
        loading: Boolean,
    })

    const appStore = useAppStore()
    const platformAuthStore = usePlatformAuthStore()
    const platformAuthUrlStore = usePlatformAuthUrlStore()

    const { initialUrlSearch } = storeToRefs(appStore)

    const checkAppViewerUrlAuthTimeout = ref(null)

    const url = computed(() => {
        return props.authRequirements.url
    })

    const useManualAuth = computed(() => initialUrlSearch.value.includes('manual_auth'))

    const checkAppViewerUrlAuthCompletedExternally = async () => {
        console.log('Checking local storage to see if user has logged in')

        // Check if completed and trigger the login event
        const urlAuthCompletedEvent = await preferences.getGlobalItem('url_auth_completed_event')
        if (urlAuthCompletedEvent) {
            const payload = JSON.parse(urlAuthCompletedEvent)

            // NOTE: Timeout required as the other window closing at the same time this window navigates
            // seems to cause a weird bug where this windows router doesn't update correctly
            setTimeout(() => {
                platformAuthStore.events.emit('login', payload)
            }, 1000)
            return
        }

        // Wait and check again
        checkAppViewerUrlAuthTimeout.value = setTimeout(() => checkAppViewerUrlAuthCompletedExternally(), 250)
    }

    const startBrowserAuth = async () => {
        if (props.loading) {
            return false
        }
        emit('loading', true)
        platformAuthUrlStore.start({ url: url.value })

        if (platformAuthUrlStore.shouldUseAppViewerUrlAuthProcess) {
            await preferences.removeGlobalItem('url_auth_completed_event')
            checkAppViewerUrlAuthCompletedExternally()
        }
    }

    // This isn't inside onMounted as we only want this to run once, not every time the component is activated
    // Automatically start browser auth if relevant
    // Prevent Browser auth automatically starting in admin
    if (!useManualAuth.value) {
        startBrowserAuth()
    }

    onUnmounted(() => {
        // Clear auth check timeout
        if (checkAppViewerUrlAuthTimeout.value !== null) {
            clearTimeout(checkAppViewerUrlAuthTimeout.value)
        }
    })
</script>
